import { createAsyncThunk } from '@reduxjs/toolkit';
import { t } from 'i18next';

import { LeaveQueueCompletionBehavior } from 'features/leave-queue/enums';
import type { LeaveQueueProperties } from 'features/leave-queue/interfaces';
import {
  requestTeamingLogoutNoticeRedirect,
  setInnerLoading,
} from 'features/app/store/app/appSlice';
import { selectIsAppUnloading } from 'features/app/store/app/selectors';
import { LogoutType } from 'features/call/call-base/enums';
import { callerSessionAnalyticsInfo } from 'features/caller-session/helpers';
import { stoppedTakingCalls } from 'features/caller-session/store';
import { leaveQueueAnalytics } from 'features/leave-queue/helpers';
import { handleError, showNotification } from 'features/notification/store';
import { finishSession } from 'features/session/store';

export const leaveQueue = createAsyncThunk(
  'leaveQueue/leaveQueue',
  async (
    {
      shouldFinishSession,
      completionBehavior = LeaveQueueCompletionBehavior.Reload,
    }: LeaveQueueProperties,
    { dispatch }
  ) => {
    dispatch(setInnerLoading(true));
    dispatch(stoppedTakingCalls());
    dispatch(callerSessionAnalyticsInfo('Stopped taking calls'));

    const logoutType = LogoutType.Normal;
    const shouldShowTeamingLogoutNotice =
      completionBehavior === LeaveQueueCompletionBehavior.TeamingLogoutNotice;

    try {
      dispatch(leaveQueueAnalytics('leaveQueue', LogoutType[logoutType]));

      if (shouldFinishSession) {
        await dispatch(
          finishSession({
            logoutType,
            shouldResetMode: !shouldShowTeamingLogoutNotice,
          })
        ).unwrap();
      }
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'leaveQueue',
        })
      );
    }

    dispatch(setInnerLoading(false));

    if (shouldShowTeamingLogoutNotice) {
      try {
        sessionStorage.clear();
      } catch (error) {
        dispatch(
          handleError({
            error,
            methodName: 'leaveQueue sessionStorage.clear',
          })
        );
      }

      dispatch(requestTeamingLogoutNoticeRedirect());

      return;
    }

    globalThis.location.reload();
  }
);

export const leaveQueueBecauseOfConnectionFailure = createAsyncThunk(
  'leaveQueue/leaveQueueBecauseOfConnectionFailure',
  async (
    { isRnsConnectionClosed }: { isRnsConnectionClosed: boolean },
    { dispatch, getState }
  ) => {
    dispatch(setInnerLoading(true));
    const logoutType = LogoutType.ConnectionLoss;
    dispatch(stoppedTakingCalls());
    dispatch(callerSessionAnalyticsInfo('Stopped taking calls'));
    const state = getState() as any;

    const isAppUnloading = selectIsAppUnloading(state);
    try {
      // no need to show notification or send analytics if app is unloading
      if (!isAppUnloading) {
        dispatch(
          leaveQueueAnalytics(
            isRnsConnectionClosed
              ? 'leaveQueueBecauseOfRnsConnectionFailed'
              : 'leaveQueueBecauseOfRegistrationFailed',
            LogoutType[logoutType]
          )
        );

        dispatch(
          showNotification({
            severity: 'error',
            title: t('somethingWentWrong'),
            message: isRnsConnectionClosed
              ? t('connectionIssue')
              : t('registrationFailed'),
          })
        );
      }
      await dispatch(finishSession({ logoutType })).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: isRnsConnectionClosed
            ? 'leaveQueueBecauseOfRnsConnectionFailed'
            : 'leaveQueueBecauseOfRegistrationFailed',
        })
      );
    } finally {
      dispatch(setInnerLoading(false));
    }
  }
);
